import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { II18nLanguageObj } from './useLanguage';
import useSmartStorage from './useSmartStorage';
import { LocalStorageKeys } from '../common/LocalStorageKeys';
import { calculateOnNet } from '../helpers/calculateShowFees';
import { resolveAffiliateCode } from '../helpers/affiliateCode';
import {
    useGetTimeslotDynamicPrices,
    useGetTimeslotPrices,
} from '../../openapi/timeslots/timeslots';

const DEFAULT_GENERIC_FIELDS: IGenericBookingFields = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
};

export interface ICustomField {
    type: string;
    name: string;
    nameI18n: II18nLanguageObj;
    description: string | string[];
    descriptionI18n: II18nLanguageObj;
    required: string;
    value: string;
}

export interface ICartItem {
    // To identify the ticket itself
    ticketTypeId: number;
    ticketTypeName: string;
    ticketTypeNameI18n: II18nLanguageObj;
    ticketOptionId: number;
    ticketOptionName: string;
    ticketOptionNameI18n: II18nLanguageObj;
    price: number;
    // The show-level default price (cents) for this combo, captured at add time.
    // Used to revert `price` when moving to a timeslot with no override/quote.
    basePrice: number;
    quantity: number;
    // fields themselves
    customFields: Array<ICustomField>;
    canBuyAlone: boolean;
    ageBand: string | null;
}

export interface ISeat {
    id: number;
    name: string;
    nameI18n: II18nLanguageObj;
    ticketTypeId: number;
    ticketTypeName: string;
    ticketTypeNameI18n: II18nLanguageObj;
}

export interface ITimeslot {
    id: number;
    startTime: string;
    activeArrangementId?: number | null;
    slotKey?: string | null;
    slotLabel?: string | null;
}

export interface IGenericBookingFields {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
}

export interface IAppliedCoupon {
    code: string;
    discountType: 'percentage' | 'fixed';
    discountValue: number;
    discountAmount: number;
    minOrderAmount?: number; // in cents, optional
}

interface ICartContext {
    timeslot: ITimeslot | null;
    setTimeslot: (timeslot: ITimeslot | null) => void;
    /**
     * Active price overrides for the selected timeslot, keyed by
     * `${ticketTypeId}:${ticketOptionId}` -> price (cents). Dynamic-pricing quote
     * when enabled, else per-timeslot custom prices, else empty (use base price).
     * Both steps read this so displayed prices match the cart and the charge.
     */
    priceOverrides: Map<string, number>;
    /** True while the selected timeslot's dynamic/timeslot price source is unresolved. */
    isPricingLoading: boolean;
    cart: Array<ICartItem>;
    setCart: (cart: Array<ICartItem>) => void;
    seats: Array<ISeat>;
    setSeats: (seats: Array<ISeat>) => void;
    genericBookingFields: IGenericBookingFields;
    setGenericBookingFields: (fields: IGenericBookingFields) => void;
    totalPrice: number;
    getFinalPrice: (showFees: any[]) => number;
    getFinalPriceWithDiscount: (showFees: any[]) => number;
    getFinalPriceAfterAdjustment: (showFees: any[]) => number;
    getTaxesAndFeesAggregated: (showFees: any[]) => {
        totalFeeAmount: number;
        totalTaxAmount: number;
    };
    appliedCoupon: IAppliedCoupon | null;
    setAppliedCoupon: (coupon: IAppliedCoupon | null) => void;
    clearCoupon: () => void;
    /** Cents added on top of the cart total by the show's verification script. */
    verificationDeltaCents: number;
    setVerificationDeltaCents: (cents: number) => void;
}
const CartContext = createContext<ICartContext>({
    cart: [],
    setCart: (_cart: Array<ICartItem>) => {},
    timeslot: null,
    setTimeslot: (_timeslot: ITimeslot | null) => {},
    priceOverrides: new Map<string, number>(),
    isPricingLoading: false,
    seats: [],
    setSeats: (_seats: Array<ISeat>) => {},
    genericBookingFields: { ...DEFAULT_GENERIC_FIELDS },
    setGenericBookingFields: (_fields: IGenericBookingFields) => {},
    totalPrice: 0,
    getFinalPrice: (_showFees: any[]) => 0,
    getFinalPriceWithDiscount: (_showFees: any[]) => 0,
    getFinalPriceAfterAdjustment: (_showFees: any[]) => 0,
    getTaxesAndFeesAggregated: (_showFees: any[]) => ({ totalFeeAmount: 0, totalTaxAmount: 0 }),
    appliedCoupon: null,
    setAppliedCoupon: (_coupon: IAppliedCoupon | null) => {},
    clearCoupon: () => {},
    verificationDeltaCents: 0,
    setVerificationDeltaCents: (_cents: number) => {},
});

export function CartContextProvider({ children }: { children: ReactNode }) {
    const [cart, setCart] = useSmartStorage<Array<ICartItem>>(LocalStorageKeys.CART, []);
    const [seats, setSeats] = useSmartStorage<Array<ISeat>>(LocalStorageKeys.SEATS, []);
    const [timeslot, setTimeslot] = useSmartStorage<ITimeslot | null>(
        LocalStorageKeys.TIMESLOT,
        null,
    );
    const [genericBookingFields, setGenericBookingFields] = useSmartStorage<IGenericBookingFields>(
        LocalStorageKeys.GENERIC_BOOKING_FIELDS,
        { ...DEFAULT_GENERIC_FIELDS },
    );
    const [appliedCoupon, setAppliedCoupon] = useSmartStorage<IAppliedCoupon | null>(
        LocalStorageKeys.COUPON,
        null,
    );
    const [resolvedPricingTimeslotId, setResolvedPricingTimeslotId] = useState<number | null>(null);
    // Verification delta is per-session and recomputed on every cart change,
    // so it lives in transient state — not localStorage. A stale value across
    // a reload would just be wrong.
    const [verificationDeltaCents, setVerificationDeltaCents] = useState(0);

    // Resolve the active price source for the selected timeslot ONCE, here, so
    // both step 1 (ticket list + cart total) and step 2 read the same numbers.
    // Dynamic pricing wins; else per-timeslot custom prices. staleTime keeps the
    // quote stable while browsing (re-selecting the same date won't re-roll it).
    const selectedTimeslotId = timeslot?.id ?? null;

    // Attribute the shown price to the same affiliate the booking will be
    // attributed to, so a wholesale-fixed affiliate's guests see the fixed
    // price they'll be charged instead of a dynamic quote that would then fail
    // the price-match check at booking.
    const affiliateCode = resolveAffiliateCode();
    const {
        data: dynamicData,
        isLoading: isDynamicPricesLoading,
        isFetching: isDynamicPricesFetching,
        isError: isDynamicPricesError,
    } = useGetTimeslotDynamicPrices(
        Number(selectedTimeslotId),
        { affiliateCode },
        {
            query: {
                enabled: !!selectedTimeslotId,
                staleTime: 10 * 60 * 1000,
                // Never re-price the guest's cart from under them on a tab focus
                // or reconnect - the resolved price holds for the session.
                refetchOnWindowFocus: false,
                refetchOnReconnect: false,
            },
        },
    );
    const dynamicEnabled = !!dynamicData?.enabled;
    const {
        data: timeslotPricesData,
        isLoading: isTimeslotPricesLoading,
        isFetching: isTimeslotPricesFetching,
        isError: isTimeslotPricesError,
    } = useGetTimeslotPrices(Number(selectedTimeslotId), {
        query: {
            enabled: !!selectedTimeslotId && dynamicData?.enabled === false,
            staleTime: 10 * 60 * 1000,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
        },
    });

    // A query error must not hold the widget in a perpetual loading state (which
    // would disable the checkout CTA forever). Treat an errored price source as
    // "no override" and fall through to the captured base price — same as when
    // dynamic pricing is off.
    const isPricingQueryLoading =
        !!selectedTimeslotId &&
        !isDynamicPricesError &&
        !isTimeslotPricesError &&
        (isDynamicPricesLoading ||
            isDynamicPricesFetching ||
            !dynamicData ||
            (dynamicData.enabled === false &&
                (isTimeslotPricesLoading || isTimeslotPricesFetching || !timeslotPricesData)));

    const priceOverridesSource = dynamicEnabled
        ? dynamicData?.prices || []
        : timeslotPricesData?.prices || [];
    const priceOverrides = new Map<string, number>(
        priceOverridesSource.map((p) => [`${p.ticketTypeId}:${p.ticketOptionId}`, p.price]),
    );

    const isCartPricingStale =
        !isPricingQueryLoading &&
        cart.length > 0 &&
        cart.some((item) => {
            const override = priceOverrides.get(`${item.ticketTypeId}:${item.ticketOptionId}`);
            const expectedPrice = override !== undefined ? override : item.basePrice;
            return expectedPrice !== undefined && expectedPrice !== item.price;
        });

    useEffect(() => {
        if (!selectedTimeslotId) {
            setResolvedPricingTimeslotId(null);
            return;
        }
        if (!isPricingQueryLoading) {
            setResolvedPricingTimeslotId(selectedTimeslotId);
        }
    }, [selectedTimeslotId, isPricingQueryLoading]);

    const isPricingLoading =
        !!selectedTimeslotId &&
        (resolvedPricingTimeslotId !== selectedTimeslotId ||
            isPricingQueryLoading ||
            isCartPricingStale);

    const isPricingError = isDynamicPricesError || isTimeslotPricesError;

    // Re-price cart lines whenever the active price source changes (new timeslot
    // or refreshed quote): price = override if present, else the captured base
    // price. Keyed on the source, not the cart, so it runs once per change and
    // never loops on quantity edits. Keeps step 1 / step 2 / charge in sync.
    useEffect(() => {
        if (isPricingQueryLoading) return;
        // On a price-fetch error, keep whatever price was already resolved
        // rather than silently rewriting every line to the base price (which
        // would drop a live dynamic quote or a manual override and then fail the
        // price-match check at booking).
        if (isPricingError) return;
        if (cart.length === 0) return;
        let changed = false;
        const next = cart.map((item) => {
            const override = priceOverrides.get(`${item.ticketTypeId}:${item.ticketOptionId}`);
            const newPrice = override !== undefined ? override : item.basePrice;
            if (newPrice !== undefined && newPrice !== item.price) {
                changed = true;
                return { ...item, price: newPrice };
            }
            return item;
        });
        if (changed) setCart(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [priceOverrides, isPricingQueryLoading, isPricingError]);

    const clearCoupon = useCallback(() => {
        setAppliedCoupon(null);
    }, [setAppliedCoupon]);

    const totalPrice = cart.reduce((acc, item) => {
        return acc + Math.floor(item.price) * item.quantity;
    }, 0);

    const getFinalPrice = useCallback(
        (showFees: any[]) => {
            // we need to calculate final price per item, since rounding of cents can go off
            let finalPrice = 0;
            for (const item of cart) {
                const { finalPrice: itemFinalPrice } = calculateOnNet(item.price, showFees, true);
                finalPrice += itemFinalPrice * item.quantity;
            }
            return finalPrice;
        },
        [cart],
    );

    const getFinalPriceWithDiscount = useCallback(
        (showFees: any[]) => {
            const price = getFinalPrice(showFees);
            if (appliedCoupon) {
                // Check minimum order amount requirement
                if (appliedCoupon.minOrderAmount && price < appliedCoupon.minOrderAmount) {
                    return price; // Don't apply discount if below minimum
                }
                // Calculate discount dynamically based on current cart value
                let discountAmount = 0;
                if (appliedCoupon.discountType === 'percentage') {
                    discountAmount = Math.floor((price * appliedCoupon.discountValue) / 100);
                } else {
                    // Fixed discount - use the stored value (in cents)
                    discountAmount = Math.min(appliedCoupon.discountValue, price);
                }
                return Math.max(0, price - discountAmount);
            }
            return price;
        },
        [getFinalPrice, appliedCoupon],
    );

    const getFinalPriceAfterAdjustment = useCallback(
        (showFees: any[]) => {
            return Math.max(0, getFinalPriceWithDiscount(showFees) + verificationDeltaCents);
        },
        [getFinalPriceWithDiscount, verificationDeltaCents],
    );

    const getTaxesAndFeesAggregated = useCallback(
        (showFees: any[]) => {
            let totalFeeAmount = 0;
            let totalTaxAmount = 0;
            for (const item of cart) {
                const { feeAmount, taxAmount } = calculateOnNet(item.price, showFees, true);
                totalFeeAmount += feeAmount * item.quantity;
                totalTaxAmount += taxAmount * item.quantity;
            }
            return { totalFeeAmount, totalTaxAmount };
        },
        [cart],
    );

    const memoizedContextValue = useMemo(
        () => ({
            cart,
            setCart,
            timeslot,
            setTimeslot,
            priceOverrides,
            isPricingLoading,
            seats,
            setSeats,
            totalPrice,
            genericBookingFields,
            setGenericBookingFields,
            getFinalPrice,
            getFinalPriceWithDiscount,
            getFinalPriceAfterAdjustment,
            getTaxesAndFeesAggregated,
            appliedCoupon,
            setAppliedCoupon,
            clearCoupon,
            verificationDeltaCents,
            setVerificationDeltaCents,
        }),
        [
            cart,
            setCart,
            timeslot,
            setTimeslot,
            priceOverrides,
            isPricingLoading,
            seats,
            setSeats,
            totalPrice,
            genericBookingFields,
            setGenericBookingFields,
            getFinalPrice,
            getFinalPriceWithDiscount,
            getFinalPriceAfterAdjustment,
            getTaxesAndFeesAggregated,
            appliedCoupon,
            setAppliedCoupon,
            clearCoupon,
            verificationDeltaCents,
        ],
    );

    return <CartContext.Provider value={memoizedContextValue}>{children}</CartContext.Provider>;
}
CartContext.displayName = 'CartContext';
export function useCart(): ICartContext {
    return useContext(CartContext);
}
